import mixpanel from "mixpanel-browser";
import { runtimeConfig } from "src/services/runtimeConfig";

// Mixpanel product analytics (project "DNC", id 4051203).
// Token comes from VITE_MIXPANEL_TOKEN — set in .env for production, left
// blank in .env.development so local dev sessions don't pollute the data.
// All exports no-op when the token is absent, so callers never need to check.
const token = import.meta.env.VITE_MIXPANEL_TOKEN;
const enabled = Boolean(token);

// Second Mixpanel project for PLG work (project "DNC PLG", id 4050576).
// In-app upgrade/upsell flows (ProductsPage, ReassignedGate paywall, ...)
// report there so the PLG team's funnels and experiments stay separate from
// core product analytics. Everything else goes to the main "DNC" project.
const plgToken = import.meta.env.VITE_MIXPANEL_PLG_TOKEN;
const plgEnabled = Boolean(plgToken);
// Named secondary instance; mixpanel.init returns it when a name is passed.
let plg: typeof mixpanel | undefined;

const sharedConfig = {
  // Both projects use the WebAPI ingestion proxy to avoid domain-based ad blockers.
  // In dev, route through Vite so the browser never hits the .NET dev certificate.
  api_host: import.meta.env.DEV ? "/mp" : `${runtimeConfig.apiUrl}/mp`,
  track_pageview: false, // we track route changes ourselves in RouteChangeListener
  persistence: "localStorage" as const,
};

if (enabled) {
  mixpanel.init(token, sharedConfig);
}
if (plgEnabled) {
  plg = mixpanel.init(plgToken, sharedConfig, "plg");
  // Earlier versions persisted this flow-only ID; remove it even on app-only visits.
  plg?.unregister("signup_session_id");
}

/** Tie all events to the logged-in user. Called on login / session restore. */
export function mixpanelIdentify(username: string, acctId: string, props: {
  acctName?: string | null;
  role?: string | null;
  isSysadmin?: boolean | null;
}): void {
  // username is unique per account in DNCScrub, so scope the distinct_id by
  // acctId to avoid collisions like "admin" existing on many accounts.
  const distinctId = `${acctId}:${username}`;
  const profile = {
    $name: username,
    acctId,
    acctName: props.acctName ?? undefined,
    role: props.role ?? undefined,
    isSysadmin: props.isSysadmin ?? undefined,
  };
  // Identify both projects so PLG upgrade events tie to the same user.
  for (const instance of [enabled ? mixpanel : undefined, plg]) {
    if (!instance) continue;
    instance.identify(distinctId);
    instance.people.set(profile);
    // Register as super properties so every event carries the account context.
    instance.register({ acctId, username });
  }
}

/** Clear identity on logout so a shared browser doesn't chain sessions. */
export function mixpanelReset(): void {
  if (enabled) mixpanel.reset();
  plg?.reset();
}

/** Track a custom event. Safe to call anywhere. */
export function mixpanelTrack(
  event: string,
  props?: Record<string, unknown>,
  options?: Record<string, unknown>,
): void {
  if (!enabled) return;
  mixpanel.track(event, props, options);
}

/** Track an SPA route change as a page view. */
export function mixpanelTrackPageView(path: string): void {
  if (!enabled) return;
  mixpanel.track_pageview({ page: path });
}

/**
 * Track an event to the DNC PLG project (in-app upgrade/upsell flows).
 * Use for paywall impressions, upgrade clicks, plan selections, etc. so the
 * PLG team's funnels live in their own project. Safe to call anywhere.
 */
export function mixpanelPlgTrack(
  event: string,
  props?: Record<string, unknown>,
  options?: Record<string, unknown>,
): void {
  plg?.track(event, props, options);
}

/** Register page-scoped PLG properties without sharing them across tabs or reloads. */
export function mixpanelPlgRegister(props: Record<string, unknown>): void {
  plg?.register(props, { persistent: false });
}

/** Remove a page-scoped PLG property when its flow ends without a page reload. */
export function mixpanelPlgUnregister(prop: string): void {
  plg?.unregister(prop, { persistent: false });
}
